import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { MethodsEnum } from "../enums/Methods.ts";
import { emitPlanBlocked } from "../utils/planEvents.ts";

interface RequestOptions {
  method?:
    | MethodsEnum.GET
    | MethodsEnum.POST
    | MethodsEnum.PUT
    | MethodsEnum.DELETE
    | MethodsEnum.PATCH;
  endpoint: string;
  data?: any;
  params?: any;
  // Opcional de proposito: sem timeout o axios espera indefinidamente, e uma
  // requisicao presa na fila do pool de conexoes do backend trava a tela sem
  // nunca dar erro. Quem usa hoje sao so as chamadas de listagem de opcoes
  // (useCirurgiaOpcoes / usePlanLimits) — as de assinatura/cartao continuam
  // sem limite, porque sao legitimamente lentas.
  timeout?: number;
}

// NUNCA ALTERAR ISSO PARA MAIN!!
const BASE_URL = "https://www.dashsis.com";
//const BASE_URL = "http://localhost:8080";

const handleService = async ({
  method = MethodsEnum.GET,
  endpoint,
  data,
  params,
  timeout,
}: RequestOptions) => {
  // Declarado fora do try de proposito: o catch tambem precisa saber se a rota
  // e publica, pra nao tratar o 401 de "credencial invalida" como sessao expirada.
  const publicRoutes = ["/usuario/login", "/setup"];
  const isPublicRoute = publicRoutes.includes(endpoint);

  try {
    const config: AxiosRequestConfig = {
      headers: {},
      params,
      ...(timeout ? { timeout } : {}),
    };

    if (!isPublicRoute) {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers!["Authorization"] = `Bearer ${token}`;
      }
    }

    const response = await axios({
      method,
      url: `${BASE_URL}${endpoint}`,
      data,
      ...config,
    });

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      console.error("Error:", axiosError.response?.data);

      const statusCode = (axiosError.response?.data as { statusCode?: string })
        ?.statusCode;

      const message = (axiosError.response?.data as { message?: string })
        ?.message;

      const errorData = (axiosError.response?.data as { data?: unknown })?.data;

      // Numa rota AUTENTICADA, qualquer 401 significa sessao invalida/expirada -
      // o backend usa varias mensagens diferentes pra isso (token expirado,
      // usuario nao encontrado, origem nao autorizada, etc.), entao checamos so
      // o status, nao o texto.
      //
      // Nas rotas publicas isso nao vale: em /usuario/login um 401 quer dizer
      // "senha errada", nao "sua sessao acabou". Antes deste guard, errar a senha
      // apagava o token, gravava "Sua sessao expirou" e recarregava a pagina
      // inteira - o usuario via a tela piscar e uma mensagem que nao tinha nada a
      // ver, enquanto a mensagem real do backend nunca chegava a ser exibida
      // (o return null abaixo impede o throw que a tela de login espera).
      if (statusCode === "401 UNAUTHORIZED" && !isPublicRoute) {
        localStorage.removeItem("token");
        localStorage.setItem(
          "session_expired_message",
          "Sua sessão expirou. Faça login novamente."
        );
        window.location.assign("/");
        return null;
      }

      // Permissao negada tambem e 403, mas nao tem nada a ver com assinatura.
      // Sem este desvio, um operador clicando em algo que a funcao dele nao
      // libera era jogado na tela de assinatura com "Assinatura inativa" - uma
      // mensagem errada sobre um problema que nao existe. O backend marca o
      // caso em data.motivo justamente para os dois poderem ser distinguidos.
      const motivo = (errorData as { motivo?: string } | undefined)?.motivo;
      if (statusCode === "403 FORBIDDEN" && motivo === "PERMISSAO_NEGADA") {
        throw new Error(message || "Você não tem permissão para esta ação.");
      }

      if (statusCode === "403 FORBIDDEN") {
        localStorage.setItem("subscription_blocked", message || "Assinatura inativa.");
        emitPlanBlocked();
        // Compara a PÁGINA atual, não o endpoint chamado: a tela /meu-plano
        // e /planos/listagem fazem chamadas a outros endpoints protegidos
        // (ex: /usuario/listar, /cirurgia, pra mostrar "uso do plano") que
        // também tomam 403 com o plano vencido. Se comparássemos só o
        // endpoint, esses 403 secundários disparavam um reload completo da
        // página em cima de si mesma -> mesmas chamadas de novo -> 403 de
        // novo -> loop infinito de reload (tela travada "carregando").
        const escapePaths = ["/meu-plano", "/planos/listagem"];
        if (!escapePaths.includes(window.location.pathname)) {
          window.location.assign("/meu-plano");
        }
        return null;
      }

      // "Erros de validação" é o texto genérico que o backend sempre devolve pra
      // qualquer campo invalido (@Valid do Spring) - o detalhe real vem em `data`
      // como um mapa campo->mensagem, que nenhuma tela lia. Junta os detalhes
      // reais numa mensagem só, em vez de mostrar o texto genérico sem contexto.
      if (message === "Erros de validação" && errorData && typeof errorData === "object") {
        const detalhes = Object.values(errorData as Record<string, string>).join(" ");
        throw new Error(detalhes || "Verifique os dados preenchidos e tente novamente.");
      }

      throw new Error(message || "Erro ao fazer a requisição");
    } else {
      console.error("Unexpected Error:", error);
      throw new Error("Erro inesperado");
    }
  }
};

export default handleService;

